import React from 'react';
import PropTypes from 'prop-types';
import useEmployeeAnalysisText from '../employeeAnalysisText';

const MUTED = 'tw-text-slate-400 dark:tw-text-slate-500';
export const GRADIENT = 'tw-from-indigo-400 tw-to-violet-600 tw-shadow-indigo-500/30';

export const workerInitials = (worker) => `${(worker.name || '').charAt(0)}${(worker.surname || '').charAt(0)}`.toLocaleUpperCase('tr-TR') || '?';

const Pending = () => <span aria-hidden="true" className="tw-inline-block tw-h-3 tw-w-12 tw-animate-pulse tw-rounded tw-bg-slate-200/80 dark:tw-bg-white/10" />;

/** Up to two role chips and "+N". */
export const RoleChips = ({ roles }) => {
  if (roles.length === 0) return <span className={MUTED}>-</span>;
  return (
    <span className="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-1">
      {roles.slice(0, 2).map((role) => (
        <span key={role} className="tw-max-w-[10rem] tw-truncate tw-rounded-full tw-bg-indigo-500/10 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-indigo-700 dark:tw-text-indigo-300">{role}</span>
      ))}
      {roles.length > 2 && (
        <span title={roles.slice(2).join(', ')} className="tw-rounded-full tw-bg-slate-500/10 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">+{roles.length - 2}</span>
      )}
    </span>
  );
};

RoleChips.propTypes = { roles: PropTypes.arrayOf(PropTypes.string).isRequired };

/** Content of one worker table cell. */
const WorkerCell = ({ columnKey, worker, detailsLoading }) => {
  const { label, formatMoney } = useEmployeeAnalysisText();
  switch (columnKey) {
    case 'employee':
      return (
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">
          <span aria-hidden="true" className={`tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-text-[10px] tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${GRADIENT}`}>
            {workerInitials(worker)}
          </span>
          <span className="tw-min-w-0 tw-truncate tw-font-semibold tw-text-slate-900 dark:tw-text-white">{`${worker.name || ''} ${worker.surname || ''}`.trim()}</span>
        </span>
      );
    case 'project':
      return (
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
          <span className="tw-shrink-0 tw-rounded-full tw-bg-sky-500/10 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-sky-700 dark:tw-text-sky-300">{label('table.projectCount', { count: worker.projects.length })}</span>
          <span className="tw-min-w-0 tw-truncate tw-text-slate-600 dark:tw-text-slate-300">{worker.projects.map((p) => p.projectTitle).join(', ')}</span>
        </span>
      );
    case 'role':
      return <RoleChips roles={worker.roles} />;
    case 'visits':
      return detailsLoading ? <Pending /> : <span className="tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{worker.totals.visits.toLocaleString()}</span>;
    case 'expense':
      return detailsLoading ? <Pending /> : <span className="tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{formatMoney(worker.totals.expense)}</span>;
    default:
      return null;
  }
};

WorkerCell.propTypes = { columnKey: PropTypes.string.isRequired, worker: PropTypes.object.isRequired, detailsLoading: PropTypes.bool };

/** One row of the card list (narrow screens). */
export const WorkerCardRow = ({ worker, detailsLoading }) => {
  const { label, formatMoney } = useEmployeeAnalysisText();
  return (
    <>
      <span aria-hidden="true" className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-text-xs tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${GRADIENT}`}>
        {workerInitials(worker)}
      </span>
      <span className="tw-min-w-0 tw-flex-1">
        <span className="tw-flex tw-min-w-0 tw-items-center tw-justify-between tw-gap-2">
          <span className="tw-min-w-0 tw-truncate tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{`${worker.name || ''} ${worker.surname || ''}`.trim()}</span>
          <span className="tw-shrink-0 tw-text-[13px] tw-font-semibold tw-tabular-nums tw-text-slate-800 dark:tw-text-slate-100">{detailsLoading ? '…' : formatMoney(worker.totals.expense)}</span>
        </span>
        <span className="tw-mt-0.5 tw-block tw-truncate tw-text-[13px] tw-text-slate-500 dark:tw-text-slate-400">
          {label('table.cardLine', { projects: worker.projects.length, visits: detailsLoading ? '…' : worker.totals.visits })}
        </span>
      </span>
    </>
  );
};

WorkerCardRow.propTypes = { worker: PropTypes.object.isRequired, detailsLoading: PropTypes.bool };

export default WorkerCell;
